import { CurrentStoreType } from '@/components/content/Header/parts/LocateStore';
import { StoreLocatorDetails } from '@/components/content/StoreLocator/components/StoreLocatorDetails/StoreLocatorDetails';
import { StoreLocatorList } from '@/components/content/StoreLocator/components/StoreLocatorList/StoreLocatorList';
import { HeaderNoStoresFoundPanel } from '@/components/content/StoreLocator/components/StoreLocatorNoStoresFoundPanel/NoStoresFoundPanel';
import { HeaderSearchStoreForm } from '@/components/content/StoreLocator/components/StoreLocatorSearchStoreForm/SearchStoreForm';
import { storeLocatorSateDropdownLinkSX } from '@/components/content/StoreLocator/styles/StoreLocatorStateDropdown/parts/link';
import {
	storeLocatorSateDropdownElementsSX,
	storeLocatorSateDropdownSX,
} from '@/components/content/StoreLocator/styles/StoreLocatorStateDropdown/storeLocatorStateDropdownStyles';
import { Store } from '@/components/content/StoreLocator/types/storeLocatorTypes';
import DynamicIcon from '@/components/icons/DynamicIcon';
import stateCitiesData from '@/data/Content/StoreLocator/storeLocatorStateCitiesList.json';
import { useLocalization } from '@/data/Localization';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { compare, formatText } from '@/utils/commons';
import { Box, Divider, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FC, FormEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS, MAX_EXP_TIME } from 'shared/custom/constants/commonConstants';
import { GeoLocationCookieType, StoreData } from 'shared/custom/types/commonTypes';
import { buildStoreCookie, getStoreIdAndStateId } from 'shared/custom/utils/cookieHandler';

export const StoreLocatorStateDropdown: FC<{
	closeDrawer: () => void;
	setOpenGeolocalizationRequestPanel: (a: boolean) => void;
	setGeoState: (a: string) => void;
	setCurrentPhysicalStoreValue: (a: string) => void;
	setCurrentStoreValues: ({ stateId, stLocId }: CurrentStoreType) => void;
	onStoreSelected: (storeData: StoreData) => void;
	geoState: string;
	currentPhysicalStoreValue: string;
	enableGeolocButton: boolean;
	GoBackButton: ({ handleClick }: { handleClick: () => void }) => JSX.Element;
}> = ({
	closeDrawer,
	setOpenGeolocalizationRequestPanel,
	setGeoState,
	setCurrentPhysicalStoreValue,
	setCurrentStoreValues,
	onStoreSelected,
	geoState,
	currentPhysicalStoreValue,
	enableGeolocButton,
	GoBackButton,
}) => {
	const { storeLocator } = useStoreLocatorState();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

	const statesData = stateCitiesData.formStatesObject;
	const cityList = stateCitiesData.formScheduleObject;
	const storesList = cityList?.map((stores) => stores.stores).flat();
	const localization = useLocalization('StoreLocator');
	const [storeDetailsInfo, setStoreDetailsInfo] = useState('');
	const selectLabel = localization.stateSelect.t();
	const [showError, setShowError] = useState(false);
	const [isSearch, setIsSearch] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [errorSearchTerm, setErrorSearchTerm] = useState('');
	const [resultSearch, setResultSearch] = useState<Store[]>([]);
	const resetSearchValues = () => {
		setIsSearch(false);
		setShowError(false);
		setResultSearch([]);
		setSearchTerm('');
		setErrorSearchTerm('');
	};
	const handleSubmit = async (e: FormEvent<HTMLFormElement> | MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		setShowError(false);
		setErrorSearchTerm('');
		const searchTermNormalize = formatText(searchTerm);
		if (searchTerm.trim() === '') {
			setSearchTerm('');
			return;
		}
		const getStores = storesList.filter(
			(hdmStore) =>
				hdmStore.zipcode.includes(searchTermNormalize) ||
				hdmStore.stLocId.includes(searchTermNormalize) ||
				formatText(hdmStore.storeName).includes(searchTermNormalize) ||
				formatText(hdmStore.city).includes(searchTermNormalize) ||
				formatText(hdmStore.address1).includes(searchTermNormalize) ||
				formatText(hdmStore.address2).includes(searchTermNormalize) ||
				formatText(hdmStore.stLocName).includes(searchTermNormalize)
		);
		if (getStores?.length !== 0)
			setResultSearch(getStores.sort((a, b) => compare(a.storeName, b.storeName)));
		else {
			setShowError(true);
			setErrorSearchTerm(searchTerm);
		}
		setIsSearch(true);
	};

	const skipNextCookieSyncRef = useRef(false);

	useEffect(() => {
		if (skipNextCookieSyncRef.current) {
			skipNextCookieSyncRef.current = false;
			return;
		}
		if (currentPhysicalStoreValue === '') return;
		const geoLocationCookie = getStoreIdAndStateId();
		if (!geoLocationCookie?.hasPreviousAnswer) return;
		if (geoLocationCookie.stLocId === currentPhysicalStoreValue) return;

		const updateCookieStore: GeoLocationCookieType = {
			...geoLocationCookie,
			stLocId: currentPhysicalStoreValue,
			stateId: geoState,
		};
		setCurrentStoreValues({ stateId: geoState, stLocId: currentPhysicalStoreValue });
		buildStoreCookie(updateCookieStore, true, MAX_EXP_TIME);
	}, [currentPhysicalStoreValue, geoState, setCurrentStoreValues]);

	useEffect(() => {
		if (
			storeLocator.selectedStore &&
			storeLocator.selectedStore?.isExternal &&
			currentPhysicalStoreValue === storeLocator.selectedStore.id
		) {
			setCurrentPhysicalStoreValue(currentPhysicalStoreValue);
		}
	}, [storeLocator.selectedStore, currentPhysicalStoreValue, setCurrentPhysicalStoreValue]);

	const handleChangeStoreSelector = (storeData: StoreData) => {
		skipNextCookieSyncRef.current = true;
		setCurrentPhysicalStoreValue(storeData.stLocId);
		onStoreSelected(storeData);
		closeDrawer();
		if (!isSearch) return;
		const findStores = cityList?.filter((states) =>
			states.stores.some((cities) => cities.stLocId === storeData.stLocId)
		)[0];
		setGeoState(findStores.stateId);
	};

	const handleChange = (event: SelectChangeEvent) => {
		setGeoState(event.target.value);
	};
	// Validate if we going to use this way or a setCookie instead
	useEffect(() => {
		document.cookie = `${'WC_stProv'}=${geoState};expires=Session;path=/;`;
		document.cookie = `${'WC_stFind'}=${'1'};expires=Session;path=/;`;
	}, [geoState]);
	return (
		<Grid container direction="column">
			{isSearch && <GoBackButton handleClick={resetSearchValues} />}
			<Typography sx={storeLocatorSateDropdownElementsSX.typographyElement}>
				{localization.searchForStoreMexico.t()}
			</Typography>
			<Grid container item sx={storeLocatorSateDropdownElementsSX.marginBottom}>
				{enableGeolocButton ? (
					<Link
						sx={storeLocatorSateDropdownLinkSX}
						onClick={() => setOpenGeolocalizationRequestPanel(true)}
						className="store-selector__location-link"
					>
						<DynamicIcon
							name={MATERIAL_SYMBOL_ICONS.myLocation}
							sx={storeLocatorSateDropdownElementsSX.myLocationIcon}
						/>
						{localization.useLocation.t()}
					</Link>
				) : null}
			</Grid>
			<HeaderSearchStoreForm
				handleSubmit={handleSubmit}
				searchTerm={searchTerm}
				setSearchTerm={setSearchTerm}
				isSearch={isSearch}
				goBackToList={resetSearchValues}
			/>
			<Divider />
			{isSearch && showError && <HeaderNoStoresFoundPanel searchText={errorSearchTerm} />}
			<Grid item>
				<FormControl size="small" sx={storeLocatorSateDropdownSX}>
					{!isSearch && (
						<Box
							sx={storeLocatorSateDropdownElementsSX.marginBottom}
							display="flex"
							alignItems="center"
							justifyContent="space-between"
						>
							<Typography fontSize={'12px'}>{localization.selectStoreFrom.t()}</Typography>
							<Select
								MenuProps={{
									style: { zIndex: '1500', maxHeight: `${isMobile ? '400px' : '450px'}` },
									disablePortal: true,
								}}
								value={geoState}
								label={selectLabel}
								onChange={handleChange}
								className="store-selector__input-select-state"
							>
								<MenuItem value="" disabled>
									<Typography variant="bodyBaseline">{selectLabel}</Typography>
								</MenuItem>
								{statesData.map(({ geonodeId, state }) => (
									<MenuItem value={geonodeId} key={geonodeId}>
										{state}
									</MenuItem>
								))}
							</Select>
						</Box>
					)}
					{geoState ? (
						<>
							{!!storeDetailsInfo ? (
								<StoreLocatorDetails
									selectedState={geoState}
									stLocId={storeDetailsInfo}
									onHandleMoreInfo={setStoreDetailsInfo}
								/>
							) : (
								<>
									{!showError && (
										<StoreLocatorList
											selectedState={geoState}
											closeDrawer={closeDrawer}
											handleChangeStoreSelector={handleChangeStoreSelector}
											selectedStore={currentPhysicalStoreValue}
											resultSearch={resultSearch}
										/>
									)}
								</>
							)}
						</>
					) : null}
				</FormControl>
			</Grid>
		</Grid>
	);
};
